import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import L from 'leaflet';
import React, { useEffect, useRef, useState } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import Store from '../store/Store';
import { IconMap, IconZoomIn } from './Icons';

export interface LeafletOverlaySetup {
	viewRef?: React.RefObject<HTMLDivElement | null>;
	exportZip?: () => Promise<void>;
	map?: L.Map;
	mapView?: { center: L.LatLng; bounds: L.LatLngBounds; diag: number; zoom: number };
	onUpdateView?: (mapView: unknown) => void;
	onInit?: (map: L.Map) => void;
	onZoom?: (zoom: number) => void;
	zoom?: number;
	co?: { polygon?: Array<{ lat: number; lng: number }> };
}

export interface LeafletOverlayProps {
	setup: LeafletOverlaySetup;
	coordinates?: Record<string, unknown>;
	onUpdateView?: (mapView: unknown) => void;
}

export default function LeafletOverlay(props: LeafletOverlayProps) {
	const setup = props.setup || ({} as LeafletOverlaySetup);
	const map = useMap();
	const [init, setInit] = useState(false);

	const addScale = () => {
		L.control
			.scale({
				metric: true,
				imperial: false,
				position: 'bottomleft',
			})
			.addTo(map);
	};

	const updateMapView = () => {
		const bounds = map.getBounds();
		setup.mapView = {
			center: map.getCenter(),
			bounds,
			diag: map.distance(bounds.getNorthEast(), bounds.getSouthWest()),
			zoom: map.getZoom(),
		};
		props.setup.onUpdateView?.(setup.mapView);
		props.onUpdateView?.(setup.mapView);
	};

	const setFullscreen = () => {
		const elem = setup.viewRef?.current;
		if (!elem) return;
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			elem.requestFullscreen();
		}
	};

	const createScreenshot = async (): Promise<string | undefined> => {
		if (!setup.map) return undefined;
		const node = document.querySelectorAll('.leaflet-container')[0];
		if (!node) return undefined;
		try {
			const canvas = await html2canvas(node as HTMLElement, {
				useCORS: true,
				ignoreElements: (e: Element) =>
					e.nodeName === 'BUTTON' ||
					e.nodeName === 'A' ||
					(e.className?.toString().startsWith?.('leaflet-control') ?? false),
			});
			return canvas.toDataURL();
		} catch (e) {
			console.error('error on createScreenshot:', e);
			return undefined;
		}
	};

	const createKML = (polygon: Array<{ lat: number; lng: number }>): string => {
		const points = polygon.map((e) => e.lng + ',' + e.lat + ',0').join('\n');
		return `<?xml version="1.0" encoding="UTF-8"?>
			<kml xmlns="http://www.opengis.net/kml/2.2">
				<Document>
					<name>KML Data Export</name>
					<Placemark>
					<name>Expanded Polygon</name>
					<Polygon>
						<outerBoundaryIs>
						<LinearRing>
							<coordinates>
								${points}
							</coordinates>
						</LinearRing>
						</outerBoundaryIs>
					</Polygon>
					</Placemark>
				</Document>
			</kml>`;
	};

	const exportZip = async () => {
		if (!setup.co?.polygon) {
			console.error('no polygon data!');
			return;
		}
		Store.setPopupMessage('info', 'Download', 'Flugdaten werden heruntergeladen ...', 0);
		const image = await createScreenshot();
		if (!image) {
			console.error('error on creating screenshot!');
			return;
		}
		const zip = new JSZip();
		zip.file('map.png', image.split(',')[1], { base64: true });
		zip.file('map.kml', createKML(setup.co.polygon));
		zip.generateAsync({ type: 'blob' }).then((blob) => {
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = 'map.zip';
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
		});
		Store.setPopupMessage('ok', 'Download', 'Flugdaten erfolgreich heruntergeladen!');
	};

	useEffect(() => {
		if (init) return;
		setup.exportZip = exportZip;
		props.setup.map = map;
		addScale();
		setInit(true);
		updateMapView();
		setup.onInit?.(map);
	}, [map]);

	useMapEvents({
		zoomend: () => {
			props.setup.zoom = map.getZoom();
			props.setup.onZoom?.(map.getZoom());
		},
		moveend: updateMapView,
	});

	return (
		<>
			<button className="leaflet-button switch-fullscreen"
				data-pw="button-switch-fullscreen"
				onClick={setFullscreen} type="button">
				<IconZoomIn />
			</button>
			<MapSwitch />
		</>
	);
}

function MapSwitch() {
	const map = useMap();
	const layers = {
		osm: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			attribution:
				'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
			minZoom: 6,
			maxZoom: 21,
		}),
		aerial: L.tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
			minZoom: 6,
			maxZoom: 21,
			subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
		}),
	};
	const layer = useRef(layers.aerial);

	useEffect(() => {
		map.addLayer(layer.current);
	}, [map]);

	return (
		<button
			className="leaflet-button switch-map"
			data-pw="button-switch-map"
			type="button"
			onClick={() => {
				map.removeLayer(layer.current);
				layer.current = layer.current === layers.osm ? layers.aerial : layers.osm;
				map.addLayer(layer.current);
				layer.current.setZIndex(0);
			}}
		>
			<IconMap />
		</button>
	);
}
